import {Box, TextField} from "@mui/material";
import {IngredientType} from "../../../models/IngredientType";
import {useTranslation} from "react-i18next";
import {useCallback} from "react";

export type IngredientInputProps = {
    ingredient: IngredientType;
    changeAmount: (amount: number) => void;
    changeUnit: (amount: string) => void;
    changeIngredientName: (name: string) => void;
    simplifiedEdit?: boolean;
}

export function IngredientInput({ingredient, changeAmount, changeUnit, changeIngredientName, simplifiedEdit = false}: IngredientInputProps) {
    const {t} = useTranslation();

    function parseIngredient(input: string) {
        const trimmed = input.trim();
        const words = trimmed.split(/\s+/);

        let amount: number | null = null;
        let unit: string | null = null;
        let ingredientWords: string[] = [];

        // Check if the first word is a number+unit combo (e.g. "90g", "1.5l")
        const combinedMatch = words[0].match(/^(\d+(?:[.,]\d+)?)([a-zA-ZäöüÄÖÜß]+)$/);
        if (combinedMatch) {
            amount = parseFloat(combinedMatch[1].replace(',', '.'));
            unit = combinedMatch[2];
            ingredientWords = words.slice(1);

        } else if (/^\d+[.,]?\d*$/.test(words[0])) {
            // first word is just a number
            amount = parseFloat(words[0].replace(',', '.'));

            if (words.length > 1 && /^[a-zäöüß]/.test(words[1])) {
                unit = words[1];
                ingredientWords = words.slice(2);
            } else {
                ingredientWords = words.slice(1);
            }

        } else {
            // no number at start
            if (words.length > 1 && /^[a-zäöüß]/.test(words[0])) {
                unit = words[0];
                ingredientWords = words.slice(1);
            } else {
                ingredientWords = words;
            }
        }

        return {
            amount: amount ?? null,
            unit: unit ?? null,
            ingredient: ingredientWords.join(" ").trim()
        };
    }

    const displayName = `${ingredient.amount ?? ''} ${ingredient.unit ?? ''} ${ingredient.name}`.trimStart();

    const changeIngredient = useCallback((value: string) => {
        let parsedIngredient = parseIngredient(value);

        if(parsedIngredient.amount) {
            changeAmount(parsedIngredient.amount);
        }

        if(parsedIngredient.unit) {
            changeUnit(parsedIngredient.unit);
        }

        changeIngredientName(parsedIngredient.ingredient);
        console.log(`Amount: ${parsedIngredient.amount}, Unit: ${parsedIngredient.unit}, Name: ${parsedIngredient.ingredient}`);
    }, [changeAmount, changeUnit, changeIngredientName]);

    if(!simplifiedEdit) {
        return (
            <div style={{width: '100%'}}>
                <TextField
                    id="ingredient-amount"
                    type="number"
                    label={<Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
                        <div style={{marginLeft: '5px'}}>{t('amount')}</div>
                    </Box>}
                    variant="outlined"
                    sx={{width: '20%'}}
                    value={ingredient.amount ? ingredient.amount : ''}
                    onChange={(e) => changeAmount(e.target.value as any as number)}
                />
                <TextField
                    id="ingredient-unit"
                    label={<Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
                        <div style={{marginLeft: '5px'}}>{t('unit')}</div>
                    </Box>}
                    variant="outlined"
                    sx={{width: '20%', marginLeft: '5px'}}
                    value={ingredient.unit ? ingredient.unit : ''}
                    onChange={(e) => changeUnit(e.target.value)}
                />
                <TextField
                    id="ingredient-name"
                    label={<Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
                        <div style={{marginLeft: '5px'}}>{t('name')}</div>
                    </Box>}
                    variant="outlined"
                    sx={{width: 'calc(60% - 10px)', marginLeft: '5px'}}
                    value={ingredient.name ? ingredient.name : ''}
                    onChange={(e) => changeIngredientName(e.target.value)}
                />
            </div>
        );
    } else {
        return (
            <TextField
                id="ingredient"
                label={<Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
                    <div style={{marginLeft: '5px'}}>{t('ingredient')}</div>
                </Box>}
                variant="outlined"
                sx={{width: '100%', minWidth: '350px', marginLeft: '5px'}}
                defaultValue={displayName}
                onBlur={(e) => changeIngredient(e.target.value)}
            />
        );
    }
}